/**
 * Budget alerts component - Shows active budget alerts
 */

import { AlertCircle, AlertTriangle, CheckCircle, X } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '../../../components/ui/alert';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { useBudgetTracking } from '../../../hooks/useBudgetTracking';
import { useBudgetAlerts } from '../../../hooks/useBudgetAlerts';
import { Skeleton } from '../../../components/ui/skeleton';

interface BudgetAlertsProps {
  companyIds: string[];
  userId: string;
  onNavigateToBudget?: () => void;
}

export function BudgetAlerts({ companyIds, userId, onNavigateToBudget }: BudgetAlertsProps) {
  const { data, loading, hasActiveBudget } = useBudgetTracking(companyIds, userId);
  const { alerts, criticalCount, warningCount, hasAlerts } = useBudgetAlerts(
    data?.categories || []
  );

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-32" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-20 w-full" />
        </CardContent>
      </Card>
    );
  }

  if (!hasActiveBudget || !data) {
    return null;
  }

  if (!hasAlerts) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Alertas de Orçamento</CardTitle>
        </CardHeader>
        <CardContent>
          <Alert className="border-success-200 bg-success-50 dark:border-success-800 dark:bg-success-950">
            <CheckCircle className="h-4 w-4 text-success-600 dark:text-success-400" />
            <AlertTitle>Tudo sob controle</AlertTitle>
            <AlertDescription>
              Nenhum alerta ativo. Seus gastos estão dentro do orçamento.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  const getAlertIcon = (severity: string) => {
    switch (severity) {
      case 'high':
        return AlertCircle;
      case 'medium':
        return AlertTriangle;
      default:
        return AlertTriangle;
    }
  };

  const getAlertVariant = (severity: string): 'default' | 'destructive' => {
    return severity === 'high' ? 'destructive' : 'default';
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <CardTitle>Alertas de Orçamento</CardTitle>
          <div className="flex flex-col items-end gap-1 shrink-0">
            {criticalCount > 0 && (
              <Badge variant="destructive" className="text-xs">
                {criticalCount} {criticalCount === 1 ? 'crítico' : 'críticos'}
              </Badge>
            )}
            {warningCount > 0 && (
              <Badge variant="outline" className="border-warning-500 text-warning-700 dark:text-warning-400 text-xs">
                {warningCount} {warningCount === 1 ? 'aviso' : 'avisos'}
              </Badge>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        {alerts.map((alert) => {
          const Icon = getAlertIcon(alert.severity);
          return (
            <Alert key={alert.id} variant={getAlertVariant(alert.severity)} className="pr-2">
              <Icon className="h-4 w-4 shrink-0" />
              <AlertTitle className="mb-1 pr-8">
                {alert.type === 'overspent' ? 'Orçamento estourado' : 'Atenção ao orçamento'}
              </AlertTitle>
              <AlertDescription className="flex items-start justify-between gap-2">
                <span className="flex-1 min-w-0 pr-8 text-sm">{alert.message}</span>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 shrink-0 absolute top-2 right-2"
                  aria-label="Dispensar alerta"
                >
                  <X className="h-4 w-4" />
                </Button>
              </AlertDescription>
            </Alert>
          );
        })}

        <Button variant="outline" size="sm" className="w-full" onClick={onNavigateToBudget}>
          Ver detalhes no orçamento
        </Button>
      </CardContent>
    </Card>
  );
}
